import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

import s from './IconButton.module.css';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /**
   * Required, not optional. An icon-only control with no accessible name is
   * invisible to a screen reader, and this component exists precisely so that
   * omission cannot compile.
   */
  label: string;
  tone?: 'default' | 'danger';
  /**
   * Fades in on hover or focus — for actions that would otherwise be clutter.
   *
   * The reveal is driven by the button's DIRECT parent being hovered, so this
   * only works when that parent is the row the user is aiming at. Inside a
   * tight wrapper — or one that already fades with its own row — leave it off:
   * two opacity gates means the control stays invisible until the cursor is
   * already on top of it, which is the same as not having the control.
   */
  quiet?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
  { label, tone = 'default', quiet = false, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        s.root,
        tone === 'danger' ? s.danger : undefined,
        quiet ? s.quiet : undefined,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
